import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import MemberSearchForm from "./components/MemberSearchForm";
import MemberSearchResults from "./components/MemberSearchResults";
import MemberSearchSelectedFilters from "./components/MemberSearchSelectedFilters";
import {
  EMPTY_MEMBER_SEARCH_CRITERIA,
  buildMemberSearchRequest,
  flattenMemberSearchSections,
  hasAdvancedMemberSearchCriteria,
  hasMemberSearchCriteria,
  hasMemberSearchRequestParams,
  hydrateMemberSearchCriteriaFromCatalogs,
  applyMemberSearchCriteriaPatch,
  resolveMemberSearchSectionPhases,
  type MemberSearchCriteria,
} from "./memberSearchQuery";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import {
  fetchMemberSearch,
  MemberSearchResponse,
  MemberSearchSectionKey,
} from "../../Services/MemberSearch";
import {
  MEMBER_SEARCH_RESULTS_PATH,
  buildMemberSearchResultsPath,
  buildProfessionalPath,
  hasMemberSearchUrlCriteria,
  readMemberSearchCriteriaFromSearchParams,
} from "../../utils/memberSearchNavigation";
import { useMemberSearchCatalogOptions } from "./useMemberSearchCatalogOptions";

const RESULTS_PAGE_SIZE = 51;

const EMPTY_RESULTS: MemberSearchResponse = {
  professionals: { items: [], hasMore: false, total: 0 },
  firms: { items: [], hasMore: false, total: 0 },
  jurisdictions: { items: [], hasMore: false, total: 0 },
};

type SectionPages = {
  professionals: number;
  firms: number;
  jurisdictions: number;
};

type SectionLoading = Record<MemberSearchSectionKey, boolean>;

const INITIAL_SECTION_PAGES: SectionPages = {
  professionals: 1,
  firms: 1,
  jurisdictions: 1,
};

const INITIAL_SECTION_LOADING: SectionLoading = {
  professionals: false,
  firms: false,
  jurisdictions: false,
};

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";

const mergeSectionResponse = (
  previous: MemberSearchResponse | null,
  patch: MemberSearchResponse,
  sections: MemberSearchSectionKey[],
): MemberSearchResponse => {
  const base = previous ?? EMPTY_RESULTS;
  return {
    professionals: sections.includes("professionals")
      ? patch.professionals
      : base.professionals,
    firms: sections.includes("firms") ? patch.firms : base.firms,
    jurisdictions: sections.includes("jurisdictions")
      ? patch.jurisdictions
      : base.jurisdictions,
    emptyReason: patch.emptyReason ?? base.emptyReason,
  };
};

const canRunMemberSearch = (criteria: MemberSearchCriteria): boolean =>
  criteria.searchWithin
    ? hasAdvancedMemberSearchCriteria(criteria)
    : hasMemberSearchCriteria(criteria);

const shouldOpenSingleProfessionalProfile = (
  results: MemberSearchResponse,
  pages: SectionPages,
  searchWithin: MemberSearchCriteria["searchWithin"],
  keyword: string,
): boolean => {
  if (pages.professionals !== 1) {
    return false;
  }
  if (results.professionals.total !== 1 || results.professionals.items.length !== 1) {
    return false;
  }
  if (searchWithin === "professional") {
    return true;
  }
  if (!keyword.trim()) {
    return false;
  }
  return results.firms.total === 0 && results.jurisdictions.total === 0;
};

const MemberSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();

  const [criteria, setCriteria] = useState<MemberSearchCriteria>(
    EMPTY_MEMBER_SEARCH_CRITERIA,
  );
  const [showValidation, setShowValidation] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<MemberSearchResponse | null>(null);
  const [submittedCriteria, setSubmittedCriteria] =
    useState<MemberSearchCriteria | null>(null);
  const [sectionPages, setSectionPages] = useState<SectionPages>(INITIAL_SECTION_PAGES);
  const [sectionLoading, setSectionLoading] = useState<SectionLoading>(
    INITIAL_SECTION_LOADING,
  );

  const sectionPagesRef = useRef(sectionPages);
  sectionPagesRef.current = sectionPages;
  const searchRequestIdRef = useRef(0);
  const searchAbortRef = useRef<AbortController | null>(null);
  const searchedUrlKeyRef = useRef<string | null>(null);

  const {
    memberFirms,
    regions,
    locations,
    practiceIndustries,
    officeLocations,
    isLoadingOfficeLocations,
    catalogs,
  } = useMemberSearchCatalogOptions(criteria.firm?.id);

  useEffect(() => {
    setPageMeta({ title: "Member Search Result" });
    return () => {
      searchAbortRef.current?.abort();
      clearPageMeta();
    };
  }, [clearPageMeta, setPageMeta]);

  const beginSearchRequest = useCallback(() => {
    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    return {
      requestId: ++searchRequestIdRef.current,
      signal: controller.signal,
    };
  }, []);

  const fetchSections = useCallback(
    async (
      nextCriteria: MemberSearchCriteria,
      pages: SectionPages,
      sections: MemberSearchSectionKey[],
      requestId: number,
      signal: AbortSignal,
    ): Promise<MemberSearchResponse | null> => {
      const searchRequest = buildMemberSearchRequest(nextCriteria);
      if (!hasMemberSearchRequestParams(searchRequest)) {
        return null;
      }

      const response = await fetchMemberSearch(searchRequest.query, {
        limit: RESULTS_PAGE_SIZE,
        lastNameOnly: searchRequest.lastNameOnly,
        lastName: searchRequest.lastName,
        firmId: searchRequest.firmId,
        regionIds: searchRequest.regionIds,
        countryIds: searchRequest.countryIds,
        practiceIndustryId: searchRequest.practiceIndustryId,
        officeId: searchRequest.officeId,
        officeIds: searchRequest.officeIds,
        professionalTitleFilter: searchRequest.professionalTitleFilter,
        serviceType: searchRequest.serviceType,
        professionalsPage: pages.professionals,
        firmsPage: pages.firms,
        jurisdictionsPage: pages.jurisdictions,
        sections,
        signal,
      });

      if (requestId !== searchRequestIdRef.current) {
        return null;
      }

      return response;
    },
    [],
  );

  const setLoadingForSections = (
    sections: MemberSearchSectionKey[],
    isLoading: boolean,
  ) => {
    setSectionLoading((previous) => {
      const next = { ...previous };
      for (const section of sections) {
        next[section] = isLoading;
      }
      return next;
    });
  };

  const runSearch = useCallback(
    async (
      nextCriteria: MemberSearchCriteria,
      pages: SectionPages = INITIAL_SECTION_PAGES,
    ) => {
      if (!canRunMemberSearch(nextCriteria)) {
        setShowValidation(true);
        setHasSubmitted(false);
        setResults(null);
        setSectionLoading(INITIAL_SECTION_LOADING);
        return;
      }

      setShowValidation(false);
      setHasSubmitted(true);
      setSubmittedCriteria(nextCriteria);
      setSectionPages(pages);
      setResults(null);
      setError(null);

      const searchRequest = buildMemberSearchRequest(nextCriteria);
      if (!hasMemberSearchRequestParams(searchRequest)) {
        setError(
          "Add a keyword or choose a firm, region, country, practice industry, office, or professional title to search.",
        );
        return;
      }

      const { requestId, signal } = beginSearchRequest();
      const phases = resolveMemberSearchSectionPhases(nextCriteria.searchWithin);
      const allSections = phases.flat();
      const keyword = nextCriteria.keyword.trim();
      const highlight =
        keyword ||
        (nextCriteria.searchWithin === "professional"
          ? nextCriteria.lastName.trim()
          : "");

      try {
        let merged: MemberSearchResponse | null = null;
        for (const sections of phases) {
          setLoadingForSections(sections, true);
          const patch = await fetchSections(
            nextCriteria,
            pages,
            sections,
            requestId,
            signal,
          );
          if (!patch) {
            return;
          }
          merged = mergeSectionResponse(merged, patch, sections);

          if (
            nextCriteria.searchWithin === "professional" &&
            shouldOpenSingleProfessionalProfile(
              merged,
              pages,
              nextCriteria.searchWithin,
              highlight,
            )
          ) {
            navigate(
              buildProfessionalPath(merged.professionals.items[0], {
                highlight,
              }),
              { replace: true },
            );
            return;
          }

          setResults(merged);
          setLoadingForSections(sections, false);
        }

        if (
          merged &&
          shouldOpenSingleProfessionalProfile(
            merged,
            pages,
            nextCriteria.searchWithin,
            highlight,
          )
        ) {
          navigate(
            buildProfessionalPath(merged.professionals.items[0], {
              highlight,
            }),
            { replace: true },
          );
          return;
        }

        setResults(merged);
      } catch (searchError) {
        if (isAbortError(searchError)) {
          return;
        }
        if (requestId !== searchRequestIdRef.current) {
          return;
        }
        console.error(searchError);
        setError("We couldn't load search results. Please try again.");
        setResults(EMPTY_RESULTS);
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setLoadingForSections(allSections, false);
        }
      }
    },
    [beginSearchRequest, fetchSections, navigate],
  );

  const fetchSingleSection = async (
    section: MemberSearchSectionKey,
    page: number,
  ) => {
    const criteriaForSearch = submittedCriteria ?? criteria;
    const nextPages = { ...sectionPagesRef.current, [section]: page };
    setSectionPages(nextPages);

    const { requestId, signal } = beginSearchRequest();
    setLoadingForSections([section], true);
    setError(null);

    try {
      const response = await fetchSections(
        criteriaForSearch,
        nextPages,
        [section],
        requestId,
        signal,
      );
      if (!response) {
        return;
      }

      setResults((previous) =>
        mergeSectionResponse(previous ?? EMPTY_RESULTS, response, [section]),
      );
    } catch (searchError) {
      if (isAbortError(searchError)) {
        return;
      }
      if (requestId !== searchRequestIdRef.current) {
        return;
      }
      console.error(searchError);
      setError("We couldn't load search results. Please try again.");
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setLoadingForSections([section], false);
      }
    }
  };

  const handleProfessionalPageChange = (page: number) => {
    void fetchSingleSection("professionals", page);
  };

  const handleFirmPageChange = (page: number) => {
    void fetchSingleSection("firms", page);
  };

  const handleJurisdictionPageChange = (page: number) => {
    void fetchSingleSection("jurisdictions", page);
  };

  const urlKey = searchParams.toString();
  const urlCriteria = useMemo(
    () => readMemberSearchCriteriaFromSearchParams(searchParams),
    [urlKey],
  );

  useEffect(() => {
    if (!hasMemberSearchUrlCriteria(urlCriteria)) {
      return;
    }
    const hydrated = hydrateMemberSearchCriteriaFromCatalogs(
      urlCriteria,
      catalogs,
    );
    setCriteria(hydrated);
    if (searchedUrlKeyRef.current === urlKey) {
      return;
    }
    searchedUrlKeyRef.current = urlKey;
    void runSearch(hydrated);
  }, [catalogs, runSearch, urlCriteria, urlKey]);

  const handleSearch = () => {
    const nextPath = buildMemberSearchResultsPath(criteria);
    const currentPath = urlKey
      ? `${MEMBER_SEARCH_RESULTS_PATH}?${urlKey}`
      : MEMBER_SEARCH_RESULTS_PATH;
    if (nextPath === currentPath || nextPath === `${location.pathname}${location.search}`) {
      void runSearch(criteria, INITIAL_SECTION_PAGES);
      return;
    }
    navigate(nextPath);
  };

  const handleCriteriaChange = (patch: Partial<MemberSearchCriteria>) => {
    let firmChanged = false;
    let nextFirm: MemberSearchCriteria["firm"];

    setCriteria((prev) => {
      const next = applyMemberSearchCriteriaPatch(prev, patch);

      if (patch.firm !== undefined) {
        const prevFirmId = prev.firm?.id?.trim() || undefined;
        const nextFirmId = patch.firm?.id?.trim() || undefined;

        if (nextFirmId !== prevFirmId) {
          firmChanged = true;
          next.officeLocation = undefined;
          next.firm = nextFirmId ? patch.firm : undefined;
          nextFirm = next.firm;
        }
      }

      return next;
    });

    if (firmChanged) {
      setSubmittedCriteria((prevSubmitted) => {
        if (!prevSubmitted) {
          return prevSubmitted;
        }
        return { ...prevSubmitted, firm: nextFirm, officeLocation: undefined };
      });
    }

    if (showValidation && canRunMemberSearch({ ...criteria, ...patch })) {
      setShowValidation(false);
    }
  };

  const visibleSections = flattenMemberSearchSections(
    submittedCriteria?.searchWithin,
  );

  return (
    <div className="flex w-full flex-col bg-white px-12 py-11">
      <MemberSearchForm
        criteria={criteria}
        onChange={handleCriteriaChange}
        onSearch={handleSearch}
        showValidation={showValidation}
        firms={memberFirms}
        regions={regions}
        locations={locations}
        practiceIndustries={practiceIndustries}
        officeLocations={officeLocations}
        officeLocationsLoading={isLoadingOfficeLocations}
      />

      <MemberSearchSelectedFilters criteria={submittedCriteria ?? criteria} />

      <MemberSearchResults
        results={results}
        sectionLoading={sectionLoading}
        error={error}
        hasSubmitted={hasSubmitted}
        keyword={submittedCriteria?.keyword ?? ""}
        sectionPages={sectionPages}
        pageSize={RESULTS_PAGE_SIZE}
        visibleSections={visibleSections}
        onProfessionalPageChange={handleProfessionalPageChange}
        onFirmPageChange={handleFirmPageChange}
        onJurisdictionPageChange={handleJurisdictionPageChange}
      />
    </div>
  );
};

export default MemberSearchPage;
